import { ref, onMounted, onBeforeUnmount } from "vue";

export default function useRecaptcha(containerRef, siteKey) {
  const recaptchaToken = ref(null);
  const recaptchaWidgetId = ref(null);
  const isRecaptchaLoaded = ref(false);
  const isInitialized = ref(false);
  const isRendering = ref(false);

  const loadRecaptchaScript = () => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha) {
        isRecaptchaLoaded.value = true;
        resolve();
        return;
      }

      if (document.querySelector("#recaptcha-script")) {
        const checkInterval = setInterval(() => {
          if (window.grecaptcha) {
            clearInterval(checkInterval);
            isRecaptchaLoaded.value = true;
            resolve();
          }
        }, 100);
        return;
      }

      const script = document.createElement("script");
      script.id = "recaptcha-script";
      script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        window.grecaptcha.ready(() => {
          isRecaptchaLoaded.value = true;
          resolve();
        });
      };

      script.onerror = () => {
        console.error("Failed to load reCAPTCHA script");
        reject(new Error("Failed to load reCAPTCHA script"));
      };

      document.head.appendChild(script);
    });
  };

  const renderRecaptcha = async () => {
    if (isRendering.value) return false;
    isRendering.value = true;

    try {
      if (!isRecaptchaLoaded.value) {
        await loadRecaptchaScript();
      }

      if (!containerRef.value || !containerRef.value.id) {
        console.error("Container ref is not ready or has no ID");
        return false;
      }

      const container = document.getElementById(containerRef.value.id);
      if (container && container.querySelector(".grecaptcha-badge")) {
        console.log("reCAPTCHA already rendered in this container");
        return true;
      }

      if (recaptchaWidgetId.value !== null) {
        window.grecaptcha.reset(recaptchaWidgetId.value);
      }

      recaptchaWidgetId.value = window.grecaptcha.render(containerRef.value, {
        sitekey: siteKey,
        callback: (token) => {
          recaptchaToken.value = token;
        },
        "expired-callback": () => {
          recaptchaToken.value = null;
        },
        "error-callback": () => {
          recaptchaToken.value = null;
        },
      });
      return true;
    } catch (error) {
      console.error("Error rendering reCAPTCHA:", error);
      return false;
    } finally {
      isRendering.value = false;
    }
  };

  const resetRecaptcha = () => {
    if (window.grecaptcha && recaptchaWidgetId.value !== null) {
      try {
        window.grecaptcha.reset(recaptchaWidgetId.value);
      } catch (error) {
        console.error("Error resetting reCAPTCHA:", error);
      }
      recaptchaToken.value = null;
    }
  };

  const getRecaptchaToken = () => {
    if (!window.grecaptcha || recaptchaWidgetId.value === null) {
      return null;
    }
    try {
      return window.grecaptcha.getResponse(recaptchaWidgetId.value) || null;
    } catch (error) {
      console.error("Error getting reCAPTCHA token:", error);
      return null;
    }
  };

  const initialize = async () => {
    if (isInitialized.value) return;
    await loadRecaptchaScript();
    isInitialized.value = true;
  };

  onMounted(async () => {
    await initialize();
    renderRecaptcha();
  });

  onBeforeUnmount(() => {
    resetRecaptcha();
  });

  return {
    recaptchaToken,
    getRecaptchaToken,
    resetRecaptcha,
    isRecaptchaLoaded,
    renderRecaptcha,
    initialize,
  };
}
